import React from 'react'
import { Modal, Button, Alert, Spinner, Badge } from 'react-bootstrap'
import { PhysicalPhone } from 'interfaces/physical-phone'
import { PnsMacMiniUserStatus } from 'interfaces/pns-mac-mini-user'
import useMacMiniUserByPhone from 'hooks/use-mac-mini-user-by-phone'
import useUpdateMacMiniUserStatus from 'hooks/use-update-mac-mini-user-status'

interface PhysicalPhoneMacMiniUserUpdateModalProps {
  show: boolean
  onHide: () => void
  physicalPhone: PhysicalPhone
  onSuccess: () => void
  onCancel: () => void
}

const PhysicalPhoneMacMiniUserUpdateModal: React.FC<
  PhysicalPhoneMacMiniUserUpdateModalProps
> = ({ show, onHide, physicalPhone, onSuccess, onCancel }) => {
  const {
    data: macMiniUser,
    isLoading: isFetchingUser,
    error: fetchError,
  } = useMacMiniUserByPhone(physicalPhone.name)

  const { mutate: updateMacMiniUserStatus, isLoading: isUpdatingUser } =
    useUpdateMacMiniUserStatus()

  const handleConfirmUpdate = () => {
    if (!macMiniUser) {
      console.error('No Mac Mini User found to update')
      return
    }

    // Update Mac Mini User status first, then trigger Physical Phone update
    updateMacMiniUserStatus(
      {
        userId: macMiniUser.id,
        status: PnsMacMiniUserStatus.RESERVED,
      },
      {
        onSuccess: () => {
          // After Mac Mini User is updated, trigger success callback
          // which will update the Physical Phone status
          onSuccess()
        },
        onError: (error) => {
          console.error('Failed to update Mac Mini User:', error)
        },
      }
    )
  }

  const getStatusVariant = (status: string) => {
    switch (status) {
      case PnsMacMiniUserStatus.AVAILABLE:
        return 'info'
      case PnsMacMiniUserStatus.RESERVED:
        return 'warning'
      case PnsMacMiniUserStatus.ACTIVE:
        return 'success'
      case PnsMacMiniUserStatus.ICLOUD_SETUP_IN_PROGRESS:
        return 'info'
      case PnsMacMiniUserStatus.INACTIVE:
        return 'secondary'
      case PnsMacMiniUserStatus.TERMINATED:
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const getStatusLabel = (status: string) => {
    return status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ')
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Physical Phone Status Update</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Status Transition Info */}
        <Alert variant="info" className="mb-4">
          <div className="fw-bold mb-2">Device: {physicalPhone.name}</div>
          <div className="mb-2">
            Status Transition: AVAILABLE → ICLOUD_SETUP_IN_PROGRESS
          </div>
          <div>Action: Reserve associated Mac Mini user for iCloud setup</div>
        </Alert>

        {/* Loading State */}
        {isFetchingUser && (
          <div className="text-center">
            <Spinner animation="border" />
            <div className="mt-2">Finding associated Mac Mini user...</div>
          </div>
        )}

        {/* Error State */}
        {fetchError && (
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            Failed to find Mac Mini user for {physicalPhone.name}:{' '}
            {fetchError instanceof Error ? fetchError.message : 'Unknown error'}
          </Alert>
        )}

        {/* Mac Mini User Found */}
        {macMiniUser && (
          <>
            <h5 className="mb-3">Mac Mini User To Be Updated</h5>

            <div
              className="border border-secondary rounded p-3 mb-3"
              style={{ backgroundColor: '#2c3034' }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0 text-light">
                  User 1{' '}
                  <Badge
                    variant={getStatusVariant(macMiniUser.status)}
                    className="ms-2"
                  >
                    {macMiniUser.status}
                  </Badge>
                </h6>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-2">
                    <strong className="text-light">Username:</strong>{' '}
                    <span className="text-light">{macMiniUser.username}</span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-2">
                    <strong className="text-light">New Status:</strong>{' '}
                    <Badge
                      variant={getStatusVariant(PnsMacMiniUserStatus.RESERVED)}
                    >
                      {getStatusLabel(PnsMacMiniUserStatus.RESERVED)}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            <Alert variant="warning">
              <strong>Note:</strong> This will reserve the Mac Mini user for
              iCloud setup. The Physical Phone will also be updated to
              ICLOUD_SETUP_IN_PROGRESS status.
            </Alert>
          </>
        )}

        {/* No Mac Mini User Found */}
        {!isFetchingUser && !fetchError && !macMiniUser && (
          <Alert variant="warning">
            <Alert.Heading>No Mac Mini User Found</Alert.Heading>
            No Mac Mini user found with username "{physicalPhone.name}". The
            Physical Phone status will be updated without affecting any Mac Mini
            user.
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={isUpdatingUser}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmUpdate}
          disabled={isUpdatingUser || isFetchingUser}
        >
          {isUpdatingUser ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Updating...
            </>
          ) : macMiniUser ? (
            'Update Both Statuses'
          ) : (
            'Update Phone Status'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PhysicalPhoneMacMiniUserUpdateModal
